import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A settings page (`kind: settings`, docs/ui-components.md §7.2, rule L-24): a module's one object
 * of policy, drawn as sections of label/value facts, each with its own «Change» — never as a
 * one-row list («تعرض 1 من 1», «لهذا الصفّ», «عرض كل الأعمدة»).
 *
 * The renderer reads and draws; the shell reads the route and opens the forms, exactly as it opens
 * a row action (the object is the row). Values are drawn by the same `Cell` a list cell is.
 */
import { useMemo } from 'react';
import { PageHead, Section, EmptyState } from "./blocks.js";
import { Button } from "./ui.js";
import { resolveLabel } from "./derive.js";
import { Cell, ManifestRefusal, Mark, Skeleton, StateNotice } from "./render.js";
import { inspect } from "./manifest.js";
/** The settings an answer holds: an object, or the first row of an array of one; null when nothing is set, undefined when it is no settings at all. */
export function settingsRowOf(body) {
    if (body === null)
        return null;
    if (Array.isArray(body)) {
        if (!body.length)
            return null;
        const first = body[0];
        return first && typeof first === 'object' && !Array.isArray(first) ? first : undefined;
    }
    return typeof body === 'object' ? body : undefined;
}
export function SettingsScreen({ manifest, result, ctx, onRun, onRetry, headless = false }) {
    const i18n = ctx.i18n;
    const problems = useMemo(() => inspect(manifest, (n) => ctx.components.has(n)).filter((p) => p.code !== 'UNKNOWN_COMPONENT'), [manifest, ctx.components]);
    if (manifest.permission && !ctx.holds(manifest.permission))
        return _jsx(StateNotice, { state: "forbidden", i18n: i18n, detail: manifest.permission });
    if (problems.length)
        return _jsx(ManifestRefusal, { problems: problems, i18n: i18n });
    const title = resolveLabel(manifest.title, manifest.title_key, i18n);
    const lede = manifest.lede || manifest.lede_key ? resolveLabel(manifest.lede, manifest.lede_key, i18n) : undefined;
    const head = headless ? null : _jsx(PageHead, { title: title, ...(lede ? { lede } : {}) });
    const sections = manifest.settings.sections;
    const columns = new Map((manifest.columns ?? []).map((c) => [c.key, c]));
    const actions = new Map((manifest.actions ?? []).map((a) => [a.key, a]));
    // Offered only to a viewer who may run it, and only where the shell can open it.
    const changer = (section) => {
        const a = section.action ? actions.get(section.action) : undefined;
        return a && onRun && (!a.permission || ctx.holds(a.permission)) ? a : null;
    };
    let body;
    if (result.state === 'loading')
        body = _jsx(Skeleton, { rows: 4, cards: sections.length, i18n: i18n });
    else if (result.state !== 'ok')
        body = _jsx(StateNotice, { state: result.state, detail: result.detail, said: result, i18n: i18n, ...(onRetry ? { onRetry } : {}) });
    else {
        const row = settingsRowOf(result.data);
        if (row === undefined)
            body = _jsx(StateNotice, { state: "unavailable", detail: i18n.t('settings.malformed'), i18n: i18n });
        else if (row === null) {
            const first = sections.map(changer).find(Boolean) ?? null;
            const empty = manifest.settings.empty;
            body = (_jsx(EmptyState, { "data-settings-empty": "", title: empty ? resolveLabel(empty.label, empty.label_key, i18n) : i18n.t('settings.empty'), action: first ? _jsx(Button, { variant: "primary", "data-settings-change": first.key, onClick: () => onRun(first, undefined), children: resolveLabel(first.label, first.label_key, i18n) }) : undefined }));
        }
        else {
            body = (_jsx("div", { className: "grid gap-4", children: sections.map((section) => {
                    const label = resolveLabel(section.label, section.label_key, i18n);
                    const action = changer(section);
                    return (_jsx(Section, { surface: "outline", level: 3, title: label, "data-settings-section": section.key, ...(section.lede || section.lede_key ? { description: resolveLabel(section.lede, section.lede_key, i18n) } : {}), ...(action ? { aside: (_jsx(Button, { size: "sm", "data-settings-change": action.key, "aria-label": i18n.t('settings.change.named', { section: label }), icon: _jsx(Mark, { name: action.icon ?? 'settings', ctx: ctx, className: "size-4" }), onClick: () => onRun(action, row), children: i18n.t('settings.change') })) } : {}), children: _jsx("dl", { className: "m-0 grid gap-x-6 gap-y-3.5 sm:grid-cols-2", children: section.facts.map((k) => {
                                const c = columns.get(k);
                                const { detail: _detail, ...plain } = c;
                                return (_jsxs("div", { className: "grid min-w-0 gap-0.5", "data-column": k, children: [_jsx("dt", { dir: "auto", className: "text-[12.5px] text-muted-foreground", children: resolveLabel(c.label, c.label_key, i18n) }), _jsx("dd", { className: "m-0 min-w-0 wrap-anywhere text-start text-[14px] font-semibold text-foreground", children: _jsx(Cell, { value: row[k], column: c.display === 'person' ? { ...plain, display: undefined } : c, ctx: ctx, row: row }) })] }, k));
                            }) }) }, section.key));
                }) }));
        }
    }
    return (_jsxs("div", { className: "grid gap-4", "data-settings-screen": manifest.screen, children: [head, body] }));
}
//# sourceMappingURL=settings.js.map